import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { catchError, filter, from, map, Observable, of, switchMap, tap } from 'rxjs';
import {
    TreatmentReturnRequest,
    TreatmentReturnRequestEditModel,
    TreatmentReturnRequestReplies,
} from '../model/treatmentReturnRequest';
import { Base64ImageExtractService } from '../service/base64-image-extractor.service';
import { logger } from '../utils/logger';
import { TreatmentReturnFileUploadCompletion, TreatmentReturnFileUploadPreparation } from './models';

@Injectable({
    providedIn: 'root',
})
export class TreatmentReturnRequestClient {
    private readonly _httpClient = inject(HttpClient);
    private readonly _externalHttpClient = new HttpClient(inject(HttpBackend));
    private readonly _base64ImageExtractor = inject(Base64ImageExtractService);

    listByTreatmentId(treatmentId: string, suppressErrors: boolean = true): Observable<TreatmentReturnRequest[]> {
        const path = `/api/treatments/${treatmentId}/return-requests`;
        const request: Observable<TreatmentReturnRequest[]> = this._httpClient.get<TreatmentReturnRequest[]>(path);
        if (!suppressErrors) return request;
        return request.pipe(
            catchError(err => {
                logger.logWarning(`Failed to request ${path}, error:\n${JSON.stringify(err)}`);
                return of<TreatmentReturnRequest[]>([]);
            })
        );
    }

    listReplies(treatmentId: string, treatmentReturnRequestId: number): Observable<TreatmentReturnRequestReplies> {
        return this._httpClient
            .get<TreatmentReturnRequestReplies>(
                `/api/treatments/${treatmentId}/return-requests/${treatmentReturnRequestId}/replies`
            )
            .pipe(catchError(() => of([])));
    }

    save(treatmentId: string, value: TreatmentReturnRequestEditModel) {
        return from(this._base64ImageExtractor.extract(value.reason ?? '')).pipe(
            switchMap(reason =>
                this._httpClient.post(`/api/treatments/${treatmentId}/return-requests`, {
                    ...value,
                    reason,
                })
            )
        );
    }

    reply(treatmentId: string, value: FormData) {
        return this._httpClient.post(`/api/treatments/${treatmentId}/return-requests:reply`, value);
    }

    prepareFileUploadToDesignService(
        treatmentId: string,
        treatmentReturnRequestId: number,
        fileUrl: string,
        fileName?: string | null
    ): Observable<TreatmentReturnFileUploadPreparation> {
        return this._httpClient.post<TreatmentReturnFileUploadPreparation>(
            `/api/treatments/${treatmentId}/return-requests/${treatmentReturnRequestId}:prepareFileUploadToDesignService`,
            { fileUrl, originalFileName: fileName }
        );
    }

    downloadFile(fileUrl: string, onProgress: (progress: number) => void): Observable<Blob> {
        return this._externalHttpClient
            .get(fileUrl, {
                observe: 'events',
                reportProgress: true,
                responseType: 'blob',
            })
            .pipe(
                tap(event => {
                    if (event.type !== HttpEventType.DownloadProgress || !event.total) return;

                    onProgress(Math.round((event.loaded * 100) / event.total));
                }),
                filter((event): event is HttpResponse<Blob> => event.type === HttpEventType.Response),
                map(event => {
                    if (event.body === null) throw new Error('The attachment response body is empty.');
                    return event.body;
                })
            );
    }

    uploadFileToDesignService(
        uploadUrl: string,
        file: Blob,
        contentType: string,
        onProgress: (progress: number) => void
    ): Observable<void> {
        const headers = contentType ? new HttpHeaders({ 'Content-Type': contentType }) : new HttpHeaders();
        return this._externalHttpClient
            .put(uploadUrl, file, {
                headers,
                observe: 'events',
                reportProgress: true,
                responseType: 'text',
            })
            .pipe(
                tap(event => {
                    if (event.type !== HttpEventType.UploadProgress || !event.total) return;

                    onProgress(Math.round((event.loaded * 100) / event.total));
                }),
                filter((event): event is HttpResponse<string> => event.type === HttpEventType.Response),
                map(() => undefined)
            );
    }

    completeFileUploadToDesignService(
        treatmentId: string,
        treatmentReturnRequestId: number,
        model: TreatmentReturnFileUploadCompletion
    ): Observable<void> {
        return this._httpClient.post<void>(
            `/api/treatments/${treatmentId}/return-requests/${treatmentReturnRequestId}:completeFileUploadToDesignService`,
            model
        );
    }
}
